/**
 * A progressão do caso no card "Preparação do pacote" (E200.F2, GUS-220, ADR-0143 §1): Documentos →
 * Leitura → Análise → Prévias → Revisão → Assinatura → Entrega, cada uma concluída, em andamento, a
 * fazer ou falhou, derivadas do pacote (`etapasDoPacote`). A situação sai em texto, não só na cor;
 * a etapa em andamento leva o círculo girando só enquanto o sistema prepara.
 */
import { Spinner } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  etapasDoPacote,
  type EtapaDoPacote,
  type PacoteDoCasoData,
  type SituacaoDaEtapa,
} from '@/lib/preparacao_do_pacote';
import { cn } from '@/lib/utils';

const t = ptBR.preparacaoDoPacote.etapas;

const COR: Readonly<Record<SituacaoDaEtapa, string>> = {
  concluida: 'text-success-700',
  atual: 'text-accent-700 font-semibold',
  pendente: 'text-fg-dim',
  falhou: 'text-danger-700 font-semibold',
};

function Marcador({ etapa, preparando }: { etapa: EtapaDoPacote; preparando: boolean }) {
  if (etapa.situacao === 'atual' && preparando) {
    return <Spinner size="sm" data-testid="pacote-etapa-spinner" />;
  }
  const sinal = etapa.situacao === 'concluida' ? '✓' : etapa.situacao === 'falhou' ? '!' : '·';
  return (
    <span aria-hidden="true" className="inline-block w-3 text-center">
      {sinal}
    </span>
  );
}

/** E206.F4 (GUS-296 item 5): "em andamento" só com a preparação rodando. */
function situacaoDita(etapa: EtapaDoPacote, preparando: boolean): string {
  if (etapa.situacao === 'atual' && !preparando) return t.situacaoDaEtapa.atualParada;
  return t.situacaoDaEtapa[etapa.situacao];
}

export function EtapasDoPacote({ pacote }: { pacote: PacoteDoCasoData }): JSX.Element {
  const etapas = etapasDoPacote(pacote);
  return (
    <ol
      aria-label={t.titulo}
      className="flex flex-wrap items-center gap-x-sc-3 gap-y-1 text-caption"
      data-testid="pacote-etapas"
    >
      {etapas.map((etapa) => (
        <li
          key={etapa.chave}
          className={cn('inline-flex items-center gap-1', COR[etapa.situacao])}
          data-testid={`pacote-etapa-${etapa.chave}`}
          data-situacao={etapa.situacao}
          aria-current={etapa.situacao === 'atual' ? 'step' : undefined}
        >
          <Marcador etapa={etapa} preparando={pacote.preparando} />
          {t[etapa.chave]}
          <span className="sr-only">{` (${situacaoDita(etapa, pacote.preparando)})`}</span>
        </li>
      ))}
    </ol>
  );
}
